import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { PermissionAccess } from '@trucktrax/trucktrax-ts-common';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { EMAIL_NOTIFICATIONS_TEXT } from '../../../constants/navConstants';
import {
  getSubscriptionPlants,
  getSubscriptionsForEmail,
  PlantOption,
  plantOptionNames,
  setSubscriptionActive,
} from '../../../services/emailSubscriptionsService';
import { EmailNotificationSubscriptionDto } from '../../../types';
import { getRegionUrlToNameMap } from '../../../util/adminUtil';
import { noop } from '../../../util/appUtil';
import { makePlantLabel } from '../../../util/emailNotificationUtil';
import { devErrorAndLog } from '../../../util/errorUtil';
import SubscriptionRowsTable from '../emailNotifications/SubscriptionRowsTable';
import styles from './UserSubscriptionsCard.module.css';

export const NO_SUBSCRIPTIONS_MESSAGE = 'This user is not subscribed to any email notifications.';

/**
 * The email notifications a user receives, shown below their profile on the user
 * detail view. Rendered through AdminResourceCard's additionalComponents slot and
 * shaped like ChecklistQuestions, the other card served that way.
 *
 * Subscriptions are keyed by email address, not by user url -- that is what the
 * shipped contract stores -- so this reads the rows matching the user's address.
 * A consequence worth knowing: changing a user's email does not carry their
 * subscriptions across, because nothing links the two.
 *
 * Gated on the Email Notifications permission rather than the Users one: the data
 * belongs to that feature, and someone who may administer users is not automatically
 * entitled to see or change who receives notifications.
 *
 * Status is all this card sets. Removing a subscription goes through the add/edit modal,
 * by unticking its plant -- there is no per-plant control here to hang a delete on, now
 * that a region is one row.
 */
export class UserSubscriptionsCard extends Component<
  UserSubscriptionsCardProps,
  UserSubscriptionsCardState
> {
  static defaultProps: Partial<UserSubscriptionsCardProps> = {
    regions: [],
    getSubscriptionsForEmail,
    getSubscriptionPlants,
    setSubscriptionActive,
    onChanged: noop,
  };

  /**
   * Bumped per load so a slow reply for an address the profile has since changed cannot
   * land last and show another user's subscriptions. The profile above resolves
   * asynchronously, so the email can change more than once in quick succession.
   */
  private loadGeneration = 0;

  state: UserSubscriptionsCardState = {
    subscriptions: [],
    plantOptions: [],
    isLoading: true,
  };

  async componentDidMount() {
    await this.load();
  }

  async componentDidUpdate(prevProps: UserSubscriptionsCardProps) {
    // The profile above loads asynchronously, so the email often arrives after mount.
    if (prevProps.email !== this.props.email) {
      await this.load();
    }
  }

  /**
   * Plant options are read alongside the subscriptions because a row cannot be named
   * without them -- the API returns a bare plant url. Both reads swallow failure to an
   * empty list, so the card degrades to urls rather than disappearing.
   */
  load = async () => {
    if (!this.props.email) {
      this.setState({ subscriptions: [], isLoading: false });
      return;
    }
    this.loadGeneration += 1;
    const generation = this.loadGeneration;

    const [subscriptions, plantOptions] = await Promise.all([
      this.props.getSubscriptionsForEmail!(this.props.email),
      this.props.getSubscriptionPlants!(),
    ]);

    if (generation === this.loadGeneration) {
      this.setState({ subscriptions, plantOptions, isLoading: false });
    }
  };

  canEdit = () => this.props.emailNotificationsPermissionAccess === PermissionAccess.Edit;

  regionNames = () => getRegionUrlToNameMap(this.props.regions ?? []);

  /** See `makePlantLabel`: a row carries no plant name, only a url. */
  plantLabel = () => makePlantLabel(plantOptionNames(this.state.plantOptions));

  onToggleActive = async (subscriptions: EmailNotificationSubscriptionDto[], active: boolean) => {
    const writable = subscriptions.filter(subscription => subscription.id != null);
    if (writable.length === 0) {
      return;
    }

    const results = await Promise.allSettled(
      writable.map(subscription => this.props.setSubscriptionActive!(subscription, active)),
    );

    const changed = new Set(
      writable.filter((_, index) => results[index].status === 'fulfilled').map(s => s.id),
    );

    if (changed.size > 0) {
      this.setState({
        subscriptions: this.state.subscriptions.map(s =>
          changed.has(s.id) ? { ...s, active } : s,
        ),
      });
      this.props.onChanged!();
    }

    const failed = results.filter(result => result.status === 'rejected');
    if (failed.length > 0) {
      this.props.devErrorAndLog!(
        `Unable to ${active ? 'activate' : 'deactivate'} ${
          failed.length === 1 ? 'a subscription' : `${failed.length} subscriptions`
        }.`,
        `User Subscriptions: ${(failed[0] as PromiseRejectedResult).reason?.toString()}`,
      );
    }
  };

  /**
   * The same table the admin screen shows when a row is expanded, so an address reads
   * identically in both places. `onRemove` is passed here and not there: this card is
   * where a user's own subscriptions are managed one at a time.
   */
  renderTable() {
    return (
      <SubscriptionRowsTable
        subscriptions={this.state.subscriptions}
        regionNames={this.regionNames()}
        plantLabel={this.plantLabel()}
        canEdit={this.canEdit()}
        onToggle={this.onToggleActive}
        // Arranged the way the add/edit modal is -- topic, then regions, then plants --
        // so an admin who has just configured an address recognises what they are
        // looking at here.
        groupByTopic
        dataTest="user-subscription"
      />
    );
  }

  render() {
    // Nothing to say to someone without the permission, and the rows are not theirs
    // to see -- so the card is absent rather than empty.
    if (!this.props.emailNotificationsPermissionAccess) {
      return null;
    }

    const { subscriptions, isLoading } = this.state;

    return (
      <div className={styles.cardWrapper}>
        <Card className={styles.cardContainer}>
          <CardContent classes={{ root: styles.cardContent }}>
            <div className={styles.header}>
              <p className={styles.headerTitle}>Email Notification Subscriptions</p>
            </div>
            <div className={styles.body}>
              {!isLoading && subscriptions.length === 0 ? (
                <div className={styles.emptyState} data-test="user-subscriptions-empty">
                  {NO_SUBSCRIPTIONS_MESSAGE}
                </div>
              ) : (
                this.renderTable()
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export interface UserSubscriptionsCardState {
  subscriptions: EmailNotificationSubscriptionDto[];
  /** Needed to name a row's plant: the API returns bare plant urls. */
  plantOptions: PlantOption[];
  isLoading: boolean;
}

const mapStateToProps = (state: any) => ({
  regions: state.regionList,
  emailNotificationsPermissionAccess: state.adminPermissionAccess[EMAIL_NOTIFICATIONS_TEXT],
});

export interface UserSubscriptionsCardProps {
  /** The user's address. Subscriptions are stored against it, not against their url. */
  email?: string;
  regions?: any[];
  emailNotificationsPermissionAccess?: string;
  /** Called after a change lands, so a parent can refresh if it needs to. */
  onChanged?: typeof noop;
  devErrorAndLog?: typeof devErrorAndLog;
  getSubscriptionsForEmail?: typeof getSubscriptionsForEmail;
  getSubscriptionPlants?: typeof getSubscriptionPlants;
  setSubscriptionActive?: typeof setSubscriptionActive;
}

export default connect<any, any, any>(mapStateToProps, { devErrorAndLog })(UserSubscriptionsCard);
